import React, { useCallback, useEffect } from 'react';
import { get } from 'lodash';
import { connect } from 'react-redux';
import { bool, func, shape, string } from 'prop-types';
import { Portal } from '@fiverr-private/ui_utils';
import { getContext } from '@fiverr-private/fiverr_context';
import { shouldDisplayFullMigrationModal } from '@fiverr-private/pro_migration';
import { useGigPageContext } from '@fiverr-private/gig_page_context';
import { useSuccessSignCtaEvent, BUSINESS_TRIGGER_CTA } from '@fiverr-private/business_success_sign_modal';
import { BriefDrawer } from '@fiverr-private/ai_brief';
import { openFloatingChat } from '@fiverr-private/go_floating_chat_triggers';
import { guardWithActivation, PhoneVerificationVariant } from "@fiverr-private/code_activation_triggers";
import { FLOW_CONTEXT_TYPES } from '../constants';
import { selectIsSellerOnline } from '../../../reducers/seller';
import { isLoggedInUser } from '../../../utils/isLoggedInUser';
import { triggerFibMigrationPopup } from '../../../utils/fibMigrationPopup';
import { useContactAvailability } from '../../../hooks';
import { onGuestClick, redirectToInboxConversation } from '../../../utils/contactSeller';
import { trackContactMeClickEvent } from '../../../utils/gtmTracking/gtmTracking';
import { updateShowFirstMessagePopup } from '../../../actions/widgets';
import { useAIBrief } from '../../../hooks/useAIBrief';
import { useRecentInteractions } from '../../../hooks/useRecentInteractions';
import { CONTACT_TRIGGER_VALUES } from '../../../reducers/packages/utils/constants';
import { consumeContactTrigger, saveContactTriggerAndRefreshPage } from '../../../utils/contactAutoTrigger/contactAutoTrigger';

const ContactTrigger = ({
    onClick,
    source,
    children,
    updateShowFirstMessagePopup,
    triggerId,
    triggerCopy,
    triggerPlacement,
    isOnline,
    aiBriefProps = {},
    shouldOpenAiBrief = false,
}) => {
    const { pathfinderWrapper, biEvents, seller, floatingChatData, general } = useGigPageContext();
    const { isContactAvailable } = useContactAvailability();
    const { checkRecentInteractions } = useRecentInteractions();

    const triggerFirstMessagePopup = useCallback(
        (showModal = true) => {
            const { isTouch } = getContext();

            if (isTouch) {
                redirectToInboxConversation(pathfinderWrapper);

                return false;
            } else if (showModal) {
                updateShowFirstMessagePopup(true);
            }

            return true;
        },
        [pathfinderWrapper, updateShowFirstMessagePopup]
    );

    const { openDrawerAiBriefDrawer, props} = useAIBrief({
        onContactSellerClick: triggerFirstMessagePopup,
    });

    const successCtaEventSource = `gig-page-${source}`;
    const handleClick = async () => {
        const triggerOptions = onClick();
        const showModal = get(triggerOptions, 'showModal', true);
        const { experience } = getContext();
        trackContactMeClickEvent();

        if (!isLoggedInUser()) {
            const afterSigningSuccess = () =>
                !experience.isBusiness && saveContactTriggerAndRefreshPage(CONTACT_TRIGGER_VALUES.MESSAGE);

            onGuestClick({
                biEvents,
                pathfinderWrapper,
                modalOptions: experience.isBusiness
                    ? {
                        source,
                        triggerCta: {
                            type: BUSINESS_TRIGGER_CTA.CONTACT,
                            source: successCtaEventSource,
                            data: { sellerName: seller.displayName },
                        },
                        afterSigningSuccess,
                    }
                    : {
                        triggerId,
                        triggerCopy,
                        triggerPlacement,
                        afterSigningSuccess,
                    },
            });

            return false;
        }

        if (!shouldOpenAiBrief) {
            await guardWithActivation({
                onSuccess: () => handleContact(showModal),
                sourceComponent: "gig_page_contact",
                phoneVerificationConfig: { variant: PhoneVerificationVariant.CONTACT },
            });
        } else {
            handleContact(showModal);
        }
    };

    const handleContact = async (showModal) => {
        if (shouldOpenAiBrief) {
            if (await checkRecentInteractions()) {
                redirectToInboxConversation(pathfinderWrapper);
                return false;
            }
            openDrawerAiBriefDrawer();
            return true;
        }

        if (shouldDisplayFullMigrationModal()) {
            triggerFibMigrationPopup({
                trigger: FLOW_CONTEXT_TYPES.contact,
                triggerSource: successCtaEventSource,
                sellerDisplayName: seller.displayName,
            });

            return false;
        }

        if (floatingChatData?.isEligible) {
            if (await checkRecentInteractions()) {
                redirectToInboxConversation(pathfinderWrapper);
                return false;
            }

            openFloatingChat({
                data: {
                    recipientUsername: seller.username,
                    showConversationSavedTooltip: floatingChatData?.showConversationSavedTooltip,
                    seller: {
                        username: seller.username,
                        name: seller.displayName || seller.username,
                        imageSrc: seller.profilePhoto,
                        isOnline,
                    },
                    initialData: {
                        gigId: general.gigId,
                        sellerId: general.sellerId,
                        entryPoint: 'gig'
                    }
                },
            });
            return false;
        }
        return triggerFirstMessagePopup(showModal);
    };

    // should have source uniq
    useSuccessSignCtaEvent({
        type: BUSINESS_TRIGGER_CTA.CONTACT,
        source: successCtaEventSource,
        callback: handleClick,
    });

    useEffect(() => {
        if (!isLoggedInUser()) {return;}
        if (!consumeContactTrigger(CONTACT_TRIGGER_VALUES.MESSAGE)) {return;}

        if (!shouldOpenAiBrief) {
            guardWithActivation({
                onSuccess: () => handleContact(true),
                sourceComponent: 'gig_page_contact',
                phoneVerificationConfig: { variant: PhoneVerificationVariant.CONTACT },
            });
        } else {
            handleContact(true);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const triggerProps = {
        onClick: handleClick,
    };

    if (!isContactAvailable) {
        return null;
    }

    return (
        <>
            {children(triggerProps)}
            <Portal>
                <BriefDrawer {...{ ...props, ...aiBriefProps }} />
            </Portal>
        </>
    );
};

ContactTrigger.propTypes = {
    onClick: func.isRequired,
    children: func,
    source: string,
    updateShowFirstMessagePopup: func,
    triggerId: string,
    triggerCopy: string,
    triggerPlacement: string,
    isOnline: bool,
    aiBriefProps: shape({
        initialText: string,
    }),
    shouldOpenAiBrief: bool,
};

const mapDispatchToProps = { updateShowFirstMessagePopup };

const mapStateToProps = (state) => ({
    isOnline: selectIsSellerOnline(state),
});

export { ContactTrigger };
export default connect(mapStateToProps, mapDispatchToProps)(ContactTrigger);
